import React, { useEffect, useState } from "react";
import { Avatar, Menu, MenuItem } from "@progress/kendo-react-layout";
import { GetToken, GetUserName, GetSession } from "./Session";
import configData from "../config.json";
import "./Header.scss";

const ACCOUNT_LINKS = [
    { text: "Account", route: "/account" },
    { text: "Settings", route: "/settings" },
    { text: "Task Board", route: "/Todos" },
    { text: "Change Password", route: "/password" },
    { text: "Logout", route: "/Logout" },
];

function readNotificationIds() {
    try {
        const ids = JSON.parse(localStorage.getItem("Notifications"));
        if (Array.isArray(ids) && ids.length === 4) return ids;
    } catch {
        // A stale browser preference must not prevent the header from loading.
    }
    return [0, 0, 0, 0];
}

export const Header = ({ onButtonClick, drawerExpanded, onNavigate }) => {
    const userName = (GetUserName() || "").trim();
    const displayName = userName.split(".").pop() || "User";
    const userInitial = Array.from(displayName)[0].toLocaleUpperCase();
    const siteId = GetSession().SiteID;
    const [support, setSupport] = useState(null);
    const [notificationError, setNotificationError] = useState("");
    const [notificationsOpen, setNotificationsOpen] = useState(false);

    useEffect(() => {
        const controller = new AbortController();
        async function loadSupportMessage() {
            try {
                const [lastSupportId] = readNotificationIds();
                const response = await fetch(configData.SERVER_URL + "apiV7/support/ViewMessage/" + lastSupportId, {
                    headers: { Authorization: "Bearer " + GetToken() },
                    signal: controller.signal,
                });
                if (!response.ok) throw new Error("Unable to load notifications.");
                setSupport(await response.json());
            } catch (error) {
                if (error.name !== "AbortError") setNotificationError("Notifications could not be loaded.");
            }
        }
        loadSupportMessage();
        return () => controller.abort();
    }, []);

    const dismissSupport = () => {
        const ids = readNotificationIds();
        ids[0] = support.supportId;
        localStorage.setItem("Notifications", JSON.stringify(ids));
        setSupport(null);
    };

    return (
        <header className="wm-header">
            <button type="button" className="wm-header__icon-button" onClick={onButtonClick}
                aria-label={drawerExpanded ? "Collapse navigation" : "Expand navigation"}
                aria-expanded={drawerExpanded} aria-controls="app-navigation">
                <i className="fa-solid fa-bars" aria-hidden="true" />
            </button>
            {siteId !== 1 && <span className="wm-header__certification">NMI S627</span>}
            <div className="wm-header__notifications">
                <button type="button" className="wm-header__icon-button"
                    aria-label={support?.description ? "Notifications: new support message" : "Notifications"}
                    aria-expanded={notificationsOpen} aria-controls="header-notifications"
                    onClick={() => setNotificationsOpen((open) => !open)}>
                    <i className="fa-solid fa-bell" aria-hidden="true" />
                    {support?.description && <span className="wm-header__notification-dot" />}
                </button>
                {notificationsOpen && (
                    <section id="header-notifications" className="wm-header__notification-panel" aria-label="Notifications"
                        onKeyDown={(event) => { if (event.key === "Escape") setNotificationsOpen(false); }}>
                        <h2>Notifications</h2>
                        <p>{notificationError || support?.description || "You're all caught up."}</p>
                        {support?.description && <button type="button" onClick={dismissSupport}>Dismiss message</button>}
                    </section>
                )}
            </div>
            <Menu className="wm-header__user-menu" onSelect={(event) => {
                if (event.item.data) onNavigate(event.item.data);
            }}>
                <MenuItem render={() => (
                    <span className="wm-header__profile" title={userName}>
                        <Avatar type="text" rounded="full" className="wm-header__avatar"
                            aria-label={"User menu for " + (userName || "User")}>{userInitial}</Avatar>
                        <span className="wm-header__user-name">{displayName}</span>
                    </span>
                )}>
                    {ACCOUNT_LINKS.map(({ text, route }) => <MenuItem key={route}
                        text={text === "Logout" ? "Logout (" + userName + ")" : text} data={route} />)}
                </MenuItem>
            </Menu>
        </header>
    );
};
